<script setup lang="ts">
import { computed, ref, watch } from "vue";
/** @ts-ignore */
import { Container, Draggable } from "vue3-smooth-dnd";
import ListingEditorPage from "./ListingEditorPage.vue";
import CustomerStoryCard from "./CustomerStoryCard.vue";
import { useListingCustomerStoriesStore } from "./composables/useListingCustomerStoriesStore";
import { useDragRotation } from "@/app/composables/useDragRotation";
import CustomerStoryEditorPage from "@/app/components/customer-stories/CustomerStoryEditorPage.vue";
import { createEmptyCustomerStoryFormModel, toCustomerStoryFormModel, toCustomerStoryUpsertPayload, type CustomerStoryFormModel } from "@/app/composables/customer-stories/customerStoryFormModel";
import { useExpertCustomerStories } from "@/app/composables/customer-stories/useExpertCustomerStories";
import { useExpertCustomerStory } from "@/app/composables/customer-stories/useExpertCustomerStory";
import { useCreateCustomerStory } from "@/app/composables/customer-stories/useCreateCustomerStory";
import { useUpdateCustomerStory } from "@/app/composables/customer-stories/useUpdateCustomerStory";
import { useDeleteCustomerStory } from "@/app/composables/customer-stories/useDeleteCustomerStory";
import type { CustomerStoryStatus } from "@shopexperts/shared";

const props = defineProps<{
    locked?: boolean;
}>();

const mode = ref<"list" | "create" | "edit">("list");
const editingStoryId = ref<number | null>(null);
const formModel = ref<CustomerStoryFormModel>(createEmptyCustomerStoryFormModel());

const storiesStore = useListingCustomerStoriesStore();
const { model: customerStoriesModel, initial: customerStoriesInitial } = storiesStore;

const storiesQuery = useExpertCustomerStories();
const storyDetailQuery = useExpertCustomerStory(editingStoryId);
const createStoryMutation = useCreateCustomerStory();
const updateStoryMutation = useUpdateCustomerStory();
const deleteStoryMutation = useDeleteCustomerStory();

watch(() => storiesQuery.data.value, (stories) => {
    if (!stories || !Array.isArray(stories)) return;

    const normalized = stories.map((story) => {
        const projectOverview = story.text_fields?.find((field) => field.handle === "project_overview")?.value || "";
        const resultSummary = story.text_fields?.find((field) => field.handle === "result_summary")?.value || "";

        return {
            id: Number(story.id) || null,
            title: story.title || "",
            slug: story.slug || "",
            content: [projectOverview, resultSummary].filter(Boolean).join("\n\n"),
            images: (story.gallery_media?.length
                ? story.gallery_media
                : story.thumbnail_media
                    ? [story.thumbnail_media]
                    : []
            ).map((media) => ({
                url: media.url,
                media_id: media.id,
                isTemporary: false,
            })),
            hired_at_shopexperts: story.hired_at_shopexperts || false,
            start_date: story.start_date || undefined,
            end_date: story.end_date || undefined,
        };
    });

    if (!storiesStore.isDirty.value) {
        customerStoriesInitial.value = normalized;
        customerStoriesModel.value = normalized;
    }
}, { immediate: true });

watch(() => storyDetailQuery.data.value, (story) => {
    if (!story || mode.value !== "edit") return;
    formModel.value = toCustomerStoryFormModel(story);
}, { immediate: true });

const visibleStories = computed(() => {
    const stories = storiesQuery.data.value || [];
    const orderedStories = customerStoriesModel.value || [];

    if (!stories.length) return [];
    if (!orderedStories.length) return stories;

    const storyMap = new Map(stories.map((story) => [Number(story.id), story]));
    const reordered = orderedStories
        .map((story) => story.id != null ? storyMap.get(Number(story.id)) : null)
        .filter((story) => story != null);

    if (reordered.length === stories.length) return reordered;

    const includedIds = new Set(reordered.map((story) => Number(story.id)));
    const remaining = stories.filter((story) => !includedIds.has(Number(story.id)));
    return [...reordered, ...remaining];
});
const isEditorBusy = computed(() =>
    storyDetailQuery.isLoading.value ||
    createStoryMutation.isPending.value ||
    updateStoryMutation.isPending.value,
);

function onActionClick() {
    editingStoryId.value = null;
    formModel.value = createEmptyCustomerStoryFormModel();
    mode.value = "create";
}

async function onSubmit(status: CustomerStoryStatus) {
    const payload = toCustomerStoryUpsertPayload(formModel.value, status);
    let savedStory;

    if (mode.value === "edit" && editingStoryId.value != null) {
        savedStory = await updateStoryMutation.mutateAsync({
            storyId: editingStoryId.value,
            payload,
        });
    } else {
        savedStory = await createStoryMutation.mutateAsync(payload);
    }

    if (!savedStory) return;

    editingStoryId.value = savedStory.id;
    formModel.value = toCustomerStoryFormModel(savedStory);
    mode.value = "edit";
}

function onEdit(storyId: number) {
    editingStoryId.value = storyId;
    mode.value = "edit";
}

async function onDelete(storyId: number) {
    await deleteStoryMutation.mutateAsync(storyId);
}

function onCancel() {
    editingStoryId.value = null;
    formModel.value = createEmptyCustomerStoryFormModel();
    mode.value = "list";
}

// Use drag rotation composable for dynamic rotation effects
const { onDragStart, onDragEnd } = useDragRotation({
    containerSelector: ".flex.flex-col.gap-4",
    baseRotation: 0,
    velocityMultiplierX: 3,
    velocityMultiplierY: 3.5,
    minAdjustment: -5.5,
    maxAdjustment: 5.5,
});

function onDrop(dropResult: { removedIndex: number | null; addedIndex: number | null }) {
    if (dropResult.removedIndex !== null && dropResult.addedIndex !== null) {
        storiesStore.reorder(dropResult.removedIndex, dropResult.addedIndex);
    }
    onDragEnd();
}
</script>

<template>
    <CustomerStoryEditorPage
        v-if="mode !== 'list'"
        v-model="formModel"
        :mode="mode === 'create' ? 'create' : 'edit'"
        :busy="isEditorBusy"
        @save="onSubmit"
        @cancel="onCancel"
    />

    <ListingEditorPage
        v-else
        heading="Case Studies"
        action="Add Case Study"
        mobile-action="Add Case Study"
        :locked="props.locked"
        :action-disabled="storiesQuery.isLoading.value"
        @action-click="onActionClick"
        empty-state-text="No case studies yet - but your past work matters! Show your skills in action, like 'Shopify redesign that boosted sales by 35%'."
        empty-state-icon="image-placeholder"
    >
        <template v-if="visibleStories.length" #content>
            <Container
                @drop="onDrop"
                @drag-start="onDragStart"
                @drag-end="onDragEnd"
                drag-handle-selector=".drag-handle"
                drag-class="dragging"
                :animation-duration="300"
                class="flex flex-col gap-4"
            >
                <Draggable
                    v-for="(item, index) in visibleStories"
                    :key="item.id || `story-${item.title}-${item.slug}-${index}`"
                >
                    <CustomerStoryCard
                        :item="item"
                        :index="index"
                        @edit="onEdit(item.id)"
                        @delete="onDelete(item.id)"
                    />
                </Draggable>
            </Container>
        </template>
    </ListingEditorPage>
</template>
